import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useEditDataFieldV2DataBulk } from "@entities/brief/lib/hooks/useEditDataFieldV2DataBulk";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { BriefNotificationActionType } from "@shared/lib/types/brief";
import {
  CustomFieldType,
  IBriefInfluencerSearchRequestV2,
  IBriefInfluencerV2,
  IDataFieldValue,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { Button, Input, Modal } from "@shared/ui";
import InputNumber from "@shared/ui/InputNumber";
import { SelectButton } from "@shared/ui/Select";
import { Check, Info, TableIcon } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

interface Props {
  selectAll?: boolean;
  checkedList: string[];
  uncheckList?: string[];
  searchPayload: Partial<IBriefInfluencerSearchRequestV2>;
  totalCount?: number;
  onSuccess?: (
    data: { id: string; briefInfluencer: IBriefInfluencerV2 }[]
  ) => void;
  onBulkSuccess?: () => void;
  visibleColumns: IFieldConfig[];
  mode?: "brief" | "campaign";
}

export function AddCustomFieldsDataButton({
  selectAll,
  checkedList,
  uncheckList,
  searchPayload,
  onSuccess,
  onBulkSuccess,
  visibleColumns,
  totalCount,
  mode = "brief",
}: Props) {
  const [isModalOpen, setisModalOpen] = useState(false);
  const [values, setValues] = useState<
    Record<string, string | number | string[]>
  >({});
  const { briefId } = useBriefStore();

  const { bulkActions, isBulkActions, isBulkActionsSuccess, bulkActionsError } =
    useBulkActions();

  const { editDataField, isEditing, isEdited } =
    useEditDataFieldV2DataBulk(briefId);

  useEffect(() => {
    if (isEdited) {
      setisModalOpen(false);
      setValues({});
    }
  }, [isEdited]);

  useEffect(() => {
    if (isBulkActionsSuccess) {
      setisModalOpen(false);
      setValues({});
      onBulkSuccess?.();
    }
  }, [isBulkActionsSuccess, bulkActionsError, onBulkSuccess]);

  const editableColumns = visibleColumns;

  const count =
    selectAll && totalCount
      ? totalCount - (uncheckList?.length ?? 0)
      : checkedList.length;

  const handleValueChange = (
    fieldKey: string,
    value: string | number | string[] | undefined
  ) => {
    if (
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      setValues((prev) => {
        const newValues = { ...prev };
        delete newValues[fieldKey];
        return newValues;
      });
    } else {
      setValues((prev) => ({ ...prev, [fieldKey]: value }));
    }
  };

  const handleApply = () => {
    if (Object.keys(values).length === 0) {
      return;
    }

    if (mode === "campaign") {
      bulkActions({
        briefId,
        actionType:
          BriefNotificationActionType.setCustomColumnsOnCampaignInfluencer,
        ids: selectAll ? [] : checkedList,
        notIncludeIds: selectAll ? uncheckList || [] : [],
        isSelectAll: Boolean(selectAll),
        filterParams: { ...searchPayload },
        actionParams: {
          dataFields: values as { [key: string]: IDataFieldValue },
        },
      });
      return;
    }

    if (selectAll) {
      bulkActions({
        briefId,
        actionType: BriefNotificationActionType.setCustomColumns,
        ids: selectAll ? [] : checkedList,
        notIncludeIds: selectAll ? uncheckList || [] : [],
        isSelectAll: Boolean(selectAll),
        filterParams: { ...searchPayload },
        actionParams: {
          dataFields: values as { [key: string]: IDataFieldValue },
        },
      });
    } else {
      editDataField({
        briefInfluencerIds: checkedList,
        dataField: values,
        handleSuccess: onSuccess ?? (() => undefined),
      });
    }
  };

  const handleCancel = () => {
    setValues({});
    setisModalOpen(false);
  };

  const hasValues = Object.keys(values).length > 0;

  return (
    <>
      <Button
        icon={<TableIcon size={16} />}
        onClick={() => setisModalOpen(true)}
      >
        Set Custom Columns
      </Button>
      <Modal
        open={isModalOpen}
        onCancel={handleCancel}
        title={null}
        footer={null}
        width={500}
        className={styles.modal}
        styles={{
          body: { padding: 0 },
        }}
      >
        <div className={styles.modalContent}>
          {/* Header */}
          <div className={styles.modalHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.headerIcon}>
                <TableIcon size={20} />
              </div>
              <div>
                <h3 className={styles.modalTitle}>Set Custom Columns</h3>
                <p className={styles.modalSubtitle}>
                  Updating data for {count} creator{count !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
            {/* <button
              onClick={handleCancel}
              className={styles.closeButton}
              type="button"
            >
              <Close size={20} />
            </button> */}
          </div>

          {/* Body */}
          <div className={styles.modalBody}>
            {editableColumns.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>
                  <Info size={24} />
                </div>
                <p className={styles.emptyText}>
                  No custom columns have been created for this campaign yet.
                </p>
              </div>
            ) : (
              <div className={styles.fieldsContainer}>
                <div className={styles.infoBanner}>
                  <Info size={16} className={styles.infoIcon} />
                  <p className={styles.infoText}>
                    Entered values will overwrite existing data for all{" "}
                    {count} selected creator{count !== 1 ? "s" : ""}. Leave fields blank to skip
                    updating them.
                  </p>
                </div>

                {editableColumns.map((field) => (
                  <div key={field.key} className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>
                      {field.displayName || field.key}
                    </label>
                    {field.type === CustomFieldType.UPLOAD ? (
                      <div className={styles.uploadNotSupported}>
                        Bulk upload for files is not supported. Use the table
                        for individual uploads.
                      </div>
                    ) : field.type === CustomFieldType.DROPDOWN &&
                      field.dropdownOptions ? (
                      <SelectButton
                        value={
                          values[field.key]
                            ? Array.isArray(values[field.key])
                              ? values[field.key]
                              : [String(values[field.key])]
                            : []
                        }
                        onChange={(val) => {
                          const newValue = Array.isArray(val) ? val : [];
                          handleValueChange(
                            field.key,
                            newValue.length > 0 ? newValue : undefined
                          );
                        }}
                        options={field.dropdownOptions.map((opt) => ({
                          label: opt,
                          value: opt,
                        }))}
                        mode="multiple"
                        placeholder="No change (Keep existing)"
                        size="middle"
                        withFooterActions
                        applyText="Apply"
                        clearText="Clear"
                        className={styles.selectButton}
                      />
                    ) : field.type === CustomFieldType.NUMBER ? (
                      <InputNumber
                        value={
                          values[field.key]
                            ? Number(values[field.key])
                            : undefined
                        }
                        onChange={(val) => {
                          handleValueChange(
                            field.key,
                            val !== null && val !== undefined ? val : undefined
                          );
                        }}
                        placeholder={`Enter ${(
                          field.displayName || field.key
                        ).toLowerCase()}...`}
                        size="middle"
                        className={styles.inputNumber}
                      />
                    ) : (
                      <Input
                        value={
                          values[field.key]
                            ? String(values[field.key])
                            : undefined
                        }
                        onChange={(e) => {
                          handleValueChange(
                            field.key,
                            e.target.value || undefined
                          );
                        }}
                        placeholder={`Enter ${(
                          field.displayName || field.key
                        ).toLowerCase()}...`}
                        size="middle"
                        className={styles.input}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.modalFooter}>
            <Button
              onClick={handleCancel}
              type="text"
              className={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              type="primary"
              loading={isBulkActions || isEditing}
              disabled={editableColumns.length === 0 || !hasValues}
              className={styles.applyButton}
              icon={<Check size={16} />}
            >
              Apply to {count} Creator{count !== 1 ? "s" : ""}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
